import makeStyles from '@mui/styles/makeStyles';
import {getPartnerAnswers, getSelfAnswers, quizIsFor} from "business/quiz";
import {color, font, type} from "common/tokens";
import QuizAnswerCard from "components/QuizAnswers/QuizAnswerCard";
import {filter, find, get, isEmpty, map, some, sortBy} from "lodash";
import React from "react";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  note: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    fontStyle: 'italic',
    color: color.TEXT,
    opacity: 0.5,
    margin: 0,
  },
  empty: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: 0,
  },
});

const fullName = person =>
  `${get(person, 'firstName', '')} ${get(person, 'lastName', '')}`.trim();

const answerFor = (answers, questionKey) =>
  find(answers, answer => answer.questionKey === questionKey);

const hasAnyAnswer = answersById =>
  some(answersById, answers => !isEmpty(answers));

const MentorshipQuizAnswerWrapper = ({quizQuestions, quizAnswers, quizRelevancy, helpText, partnerDetails, isLearningHub}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const selfAnswers = getSelfAnswers(isLearningHub, quizAnswers);
  const partnerAnswers = getPartnerAnswers(quizAnswers);

  // Built from the group roster rather than from the answers, so somebody who
  // has not replied still gets a row.
  const selfPeople = get(partnerDetails, 'userDetails', []);
  const groupPeople = get(partnerDetails, 'partnerDetails', []);

  const rowsForQuestion = question => {
    const selfRows = map(selfPeople, person => ({
      key: `self-${person.id}`,
      name: fullName(person),
      label: t('you'),
      optional: !quizIsFor(quizRelevancy, person),
      discontinued: person.applicationStatus === 'Discontinued',
      image: get(person, 'picture'),
      quizAnswer: answerFor(get(selfAnswers, `[${person.id}]`), question.key),
    }));

    const groupRows = map(groupPeople, person => ({
      key: `partner-${person.id}`,
      name: fullName(person),
      optional: !quizIsFor(quizRelevancy, person),
      discontinued: person.applicationStatus === 'Discontinued',
      image: get(person, 'picture'),
      quizAnswer: answerFor(get(partnerAnswers, `[${person.id}]`), question.key),
    }));

    // Someone who has left the group is not waited on, but a reply they gave is
    // still shown.
    const rows = filter([...selfRows, ...groupRows], row => row.quizAnswer || !row.discontinued);

    // Replies first, the people still to answer last — as in the design, so the
    // card is read for what it holds before what it is missing. Those the quiz
    // is not set for come after the people it is waiting on.
    return sortBy(rows, row => (row.quizAnswer ? 0 : row.optional ? 2 : 1));
  };

  if (!hasAnyAnswer(selfAnswers) && !hasAnyAnswer(partnerAnswers)) {
    return <p className={classes.empty}>{t('noAnswerYet')}</p>;
  }

  return (
    <div className={classes.list}>
      {!isEmpty(helpText) && <p className={classes.note}>{helpText}</p>}

      {quizQuestions.map((question, index) => (
        <QuizAnswerCard
          key={question.key || index}
          index={index + 1}
          question={question}
          rows={rowsForQuestion(question)}
        />
      ))}
    </div>
  );
};

export default MentorshipQuizAnswerWrapper;
